import { ChangeDetectorRef, Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ModalOptions, NzModalService } from 'ng-zorro-antd/modal';
import { EmailService } from '@core/services/email.service';
import { MessageService } from '@core/services/message.service';
import { EmailModalComponent } from './email-modal/email-modal.component';
import { EmailInativarModalComponent } from './email-inativar-modal/email-inativar-modal.component';
import { DebouncedSearch, MIN_CHARS_BUSCA } from '@shared/util/debounced-search.util';

@Component({
  selector: 'app-email-search-select',
  templateUrl: './email-search-select.component.html',
  styleUrls: ['./email-search-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmailSearchSelectComponent),
      multi: true,
    },
  ],
})
export class EmailSearchSelectComponent implements ControlValueAccessor, OnDestroy {
  @Input() multiple = false;
  @Input() placeholder = 'Digite ao menos 3 letras do e-mail';
  @Input() size: 'large' | 'default' | 'small' = 'large';

  nzFilterOption = (): boolean => true;
  emailList: any[] = [];
  buscaAtual = '';
  value: string | string[] | null = null;
  disabled = false;

  /**
   * Cache por e-mail que sobrevive a novas buscas (emailList é substituída a cada busca, inclusive
   * quando o nz-select limpa o texto digitado após uma seleção). O template de valor selecionado
   * (nzCustomTemplate) depende desse cache pra continuar mostrando o status de INATIVO corretamente.
   */
  private itensConhecidos = new Map<string, any>();
  private escrevendoValor = false;
  private readonly buscaDebounced = new DebouncedSearch();

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private emailService: EmailService,
    private modalService: NzModalService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.buscaDebounced.cancelPending();
  }

  writeValue(value: string | string[] | null): void {
    this.escrevendoValor = true;
    this.value = this.normalizarValor(value);
    this.garantirOpcoesDoValor();
    this.cdr.markForCheck();
    queueMicrotask(() => {
      this.escrevendoValor = false;
    });
  }

  private garantirOpcoesDoValor(): void {
    if (!this.value) {
      return;
    }
    const emailsAtuais = Array.isArray(this.value) ? this.value : [this.value];
    emailsAtuais.forEach((email) => {
      if (!this.itensConhecidos.has(email)) {
        this.registrarItens([{ email }]);
      }
      const item = this.itensConhecidos.get(email);
      if (item && !this.emailList.some((atual) => atual?.email === email)) {
        this.emailList = [...this.emailList, item];
      }
    });
    emailsAtuais
      .filter((email) => !this.itensConhecidos.get(email)?.id)
      .forEach((email) => this.resolverStatusEmail(email));
  }

  private registrarItens(itens: any[]): void {
    itens.forEach((item) => this.itensConhecidos.set(item.email, item));
  }

  /**
   * Ao carregar um valor já existente (ex.: edição de um contato salvo), resolve o registro real do
   * e-mail (id, dataInativo) para que o indicador de INATIVO apareça mesmo sem o usuário buscar de novo.
   */
  private resolverStatusEmail(email: string): void {
    this.emailService.comboPaginado(email).subscribe((data) => {
      const encontrado = (data ?? []).find((item: any) => item.email === email);
      if (encontrado) {
        this.itensConhecidos.set(email, encontrado);
        const index = this.emailList.findIndex((item) => item.email === email);
        if (index !== -1) {
          this.emailList[index] = encontrado;
        }
      }
    });
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSearch(value: string): void {
    this.buscaAtual = value;
    const emailDigitado = value?.trim();

    if (!emailDigitado || emailDigitado.length < MIN_CHARS_BUSCA) {
      this.buscaDebounced.cancelPending();
      this.emailList = this.incluirEmailsSelecionados(this.incluirEmailDigitado([], emailDigitado ?? ''));
      return;
    }

    this.buscaDebounced.run(emailDigitado, (termo) =>
      this.emailService.comboPaginado(termo).subscribe((data) => {
        this.emailList = this.incluirEmailsSelecionados(this.incluirEmailDigitado(data ?? [], termo));
        this.registrarItens(this.emailList);
      }),
    );
  }

  onValueChange(value: string | string[]): void {
    const valor = this.normalizarValor(value);
    if (this.escrevendoValor && this.mesmoValor(valor, this.value)) {
      return;
    }
    this.value = valor;
    this.onChange(valor);
    this.onTouched();
  }

  private normalizarValor(value: any): string | string[] | null {
    if (value == null || value === '') {
      return null;
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.emailComoTexto(item)).filter((item): item is string => !!item);
    }
    return this.emailComoTexto(value);
  }

  private emailComoTexto(email: any): string | null {
    if (!email) {
      return null;
    }
    if (typeof email === 'string') {
      return email;
    }
    return email.email ?? null;
  }

  private pareceEmail(valor: string): boolean {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(valor);
  }

  private incluirEmailDigitado(lista: any[], emailDigitado: string): any[] {
    if (!this.pareceEmail(emailDigitado)) {
      return lista;
    }
    const jaExiste = lista.some((item) => item?.email?.toLowerCase() === emailDigitado.toLowerCase());
    if (jaExiste) {
      return lista;
    }
    return [{ email: emailDigitado, naoCadastrado: true }, ...lista];
  }

  private incluirEmailsSelecionados(lista: any[]): any[] {
    const selecionados = Array.isArray(this.value) ? this.value : (this.value ? [this.value] : []);
    let resultado = [...lista];
    selecionados.forEach((email) => {
      if (!resultado.some((item) => item?.email === email)) {
        resultado = [this.itensConhecidos.get(email) || { email }, ...resultado];
      }
    });
    return resultado;
  }

  private mesmoValor(a: string | string[] | null, b: string | string[] | null): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  findItem(email: string): any {
    return this.itensConhecidos.get(email);
  }

  isInativo(email: string): boolean {
    return !!this.findItem(email)?.dataInativo;
  }

  inativarEmail(item: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!item?.id || item.dataInativo) {
      return;
    }

    const config: ModalOptions<EmailInativarModalComponent> = {
      nzTitle: 'Inativar e-mail',
      nzContent: EmailInativarModalComponent,
      nzCentered: true,
      nzFooter: null,
      nzMaskClosable: false,
      nzWidth: '30vw',
      nzClosable: false,
    };
    const modalRef = this.modalService.create(config);
    modalRef.componentInstance!.email = item.email;

    modalRef.afterClose.subscribe((justificativa: string | undefined) => {
      if (!justificativa) {
        return;
      }

      this.emailService.inativar(item.id, justificativa).subscribe((dto: any) => {
        item.dataInativo = dto.dataInativo;
        item.justificativaInativacao = dto.justificativaInativacao;
        this.itensConhecidos.set(item.email, item);
        this.messageService.success('E-mail inativado com sucesso!');
      });
    });
  }

  abrirCadastro(emailDigitado: string = this.buscaAtual, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const config: ModalOptions<EmailModalComponent> = {
      nzTitle: 'Cadastrar e-mail',
      nzContent: EmailModalComponent,
      nzCentered: true,
      nzFooter: null,
      nzMaskClosable: false,
      nzWidth: '30vw',
      nzClosable: false,
    };
    const modalRef = this.modalService.create(config);
    modalRef.componentInstance!.emailDigitado = emailDigitado;

    modalRef.afterClose.subscribe((result: any) => {
      if (result?.email) {
        this.registrarItens([result]);
        this.emailList = [...this.emailList.filter((item) => item?.email !== result.email), result];

        if (this.multiple) {
          const valorAtual = Array.isArray(this.value) ? this.value : [];
          this.onValueChange([...valorAtual, result.email]);
        } else {
          this.onValueChange(result.email);
        }
      }
    });
  }
}
